import React, { useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Linking,
  Platform,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import {
  getPromoShareMessage,
  getPromoShareSenderName,
  getPromoShareText,
} from '../../data/promoSocialDemo';
import { submitShareEmail, submitShare } from '../../services/socialBannerApi';

export default function PromoShareSheet({ visible, item, onClose, currentUser }) {
  const [copied, setCopied] = useState(false);
  const [isEmail, setIsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const narasi = getPromoShareText(item);
  const senderName = getPromoShareSenderName(currentUser);
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleShare = async (via) => {
    const response = await submitShare(item.id, via);
    if(response && response?.url) {
      const message = getPromoShareMessage(item, response.url, { senderName });

      if( via === 'copy' ) {
        try {
          await Clipboard.setStringAsync(message);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch (e) {
          if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }
        }
      } else if(via === 'whatsapp') {
        Linking.openURL(`https://wa.me/?text=${encodeURIComponent(message)}`).catch(() => {});
        handleCloseShare();
      }
    }
  };

  const handleEmail = async () => {
    setSending(true);

    try {
      const result = await submitShareEmail(item.id, email);
      if( !result.success ) {
        throw new Error(result.message);
      }

      setEmail('');
      setIsEmail(false);
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    } catch(e) {
      setEmailError(e.message);
      setTimeout(() => setEmailError(''), 5000);
    } finally {
      setSending(false);
    }
  }

  const showEmailError = (errorMsg) => {
    setEmailError(errorMsg);
    setTimeout(() => setEmailError(''), 5000);
  }

  const handleCloseShare = () => {
    setEmailError('');
    setIsEmail(false);
    setSending(false);
    setSent(false);
    onClose();
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleCloseShare}>
      <View style={styles.viewport}>
        <TouchableOpacity style={styles.outside} activeOpacity={1} onPress={handleCloseShare} />
        <View style={styles.column}>
          <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={handleCloseShare} />
          <View style={styles.sheet}>
            <Text style={styles.title}>Bagikan</Text>
            <Text style={styles.subtitle} numberOfLines={2}>
              {narasi}
            </Text>

            {!isEmail ? (
              <>
                <TouchableOpacity style={styles.row} onPress={() => handleShare('copy')} activeOpacity={0.7}>
                  <View style={[styles.iconWrap, { backgroundColor: '#F1F5F9' }]}>
                    <Ionicons name="link-outline" size={20} color="#1E293B" />
                  </View>
                  <Text style={styles.rowLabel}>{copied ? 'Tersalin' : 'Salin tautan'}</Text>
                  {copied && (
                    <View style={styles.checkWrap}>
                      <Ionicons name="checkmark" size={20} color="#16A34A" />
                    </View>
                  )}
                </TouchableOpacity>

                <TouchableOpacity style={styles.row} onPress={() => handleShare('whatsapp')} activeOpacity={0.7}>
                  <View style={[styles.iconWrap, { backgroundColor: '#DCFCE7' }]}>
                    <Ionicons name="logo-whatsapp" size={20} color="#16A34A" />
                  </View>
                  <Text style={styles.rowLabel}>WhatsApp</Text>
                </TouchableOpacity>

                <TouchableOpacity style={styles.row} onPress={() => setIsEmail(true)} activeOpacity={0.7}>
                  <View style={[styles.iconWrap, { backgroundColor: '#E0F2FE' }]}>
                    <Ionicons name="mail-outline" size={20} color="#0284C7" />
                  </View>
                  <Text style={styles.rowLabel}>{sent ? 'Terkirim' : 'Email'}</Text>
                  {sent && (
                    <View style={styles.checkWrap}>
                      <Ionicons name="checkmark" size={20} color="#16A34A" />
                    </View>
                  )}
                </TouchableOpacity>
              </>
            ) : (
              <>
                <View style={[styles.emailContainer, (sending && styles.emailContainerDisabled)]}>
                  <TextInput
                    style={styles.email}
                    placeholder={'Ketikkan email disini'}
                    placeholderTextColor="#94A3B8"
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!sending}
                  />
                </View>
                {emailError && (
                  <Text style={styles.emailError}>{emailError}</Text>
                )}
              </>
            )}

            <View style={styles.actions}>
              <TouchableOpacity style={styles.cancel} onPress={handleCloseShare} activeOpacity={0.7}>
                <Text style={styles.cancelText}>Batal</Text>
              </TouchableOpacity>
              {isEmail && (
                <TouchableOpacity style={[styles.send, (sending && styles.sendDisabled)]} onPress={handleEmail} activeOpacity={0.7} disabled={sending}>
                  <Text style={styles.sendText}>{sending ? 'Mengirim...' : 'Kirim Email'}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  viewport: {
    flex: 1,
    alignItems: 'center',
  },
  outside: {
    ...StyleSheet.absoluteFillObject,
  },
  column: {
    width: '100%',
    maxWidth: 430,
    height: '100%',
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  sheet: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 24,
    zIndex: 2,
  },
  title: {
    fontSize: 16,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#0F172A',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowLabel: {
    fontSize: 15,
    fontFamily: 'Poppins',
    color: '#0F172A',
  },
  cancel: {
    flex: 1,
    marginTop: 8,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
  },
  cancelText: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#475569',
  },
  emailContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    padding: 8,
  },
  emailContainerDisabled: {
    backgroundColor: '#F8FAFC',
  },
  email: {
    flex: 1,
    fontSize: 14,
    color: '#1E293B',
    fontFamily: 'Poppins',
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  send: {
    flex: 1,
    marginTop: 8,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#1E293B',
    borderRadius: 10,
  },
  sendDisabled: {
    opacity: 0.7,
  },
  sendText: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#FFFFFF',
  },
  emailError: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#FF0000',
  },
  checkWrap: {
    flex: 1,
    flexDirection: 'row-reverse',
    width: '100%',
  },
});
